/*
 * Click-and-hold hearts  ·  assets/hoh-heart-hold.js
 *
 * A pointerdown on any heart button starts an accelerating spend
 * stream. First heart fires immediately; subsequent hearts fire at a
 * tightening interval (250ms → 30ms over 3s).
 *
 * Why this matters
 * ----------------
 * On mobile, spam-tapping rapid hearts caused the modal layer to
 * glitch. Hold-to-spend replaces the spam pattern with a single
 * sustained gesture. Quick-tap users still work — a tap fires exactly
 * one heart on pointerdown and stops on pointerup before the next
 * tick.
 *
 * Visual escalation: the button picks up "frenzy" classes at 10 / 30 /
 * 60 hearts in one hold for progressively wilder pulses + halo. CSS
 * defines the keyframes.
 *
 * Routes:
 *   .hoh-heart-btn (main grid card)        → window.hohHeart(id, btn)
 *   #hoh-detail-heart (detail modal)       → window.hohHeartDetail()
 *   .hoh-detail-heart-btn (alt class)      → same as above
 *   .hoh-fresh-card-heart (fresh face)     → window.hohHeart via the
 *                                            quick-heart wrapper
 */
(function(){
  'use strict';

  /* Click-and-hold cadence — tuned for "satisfying spend" rather than
     gentle drip. The ramp finishes inside the first second so a hold
     reaches its peak speed almost immediately, and the ultra phase
     kicks in shortly after for sustained holds. The rate never slows
     once peaked — we keep firing at ULTRA_INTERVAL until the user
     releases or their wallet is empty (server-side cap stops the loop).

     ┌─────────────────────────────────────────────────────────────┐
     │ SPEED-RESET CONTRACT                                         │
     │ The ramp lives entirely inside ONE continuous hold on ONE    │
     │ hero. Every fresh pointerdown captures a new startMs, so:    │
     │   • Release → next press starts at INITIAL_INTERVAL again    │
     │   • Switch hero (different charId) → old hold force-stops,    │
     │     new hold starts at INITIAL_INTERVAL                       │
     │   • Same-hero re-press while old hold is still in flight →   │
     │     ignored (dedupe; the in-flight hold is what's running)    │
     │ Don't add module-level "lastSpeed" caching here. The reset    │
     │ behavior is what makes the spend feel intentional.            │
     └─────────────────────────────────────────────────────────────┘ */
  /* Cadence retuned for slower, more intentional spending. The old
     ramp peaked in under a second and could drain a 20-heart wallet
     in ~3 seconds — too aggressive. New shape: gentle baseline (~3
     hearts/sec) for the first 4 seconds, then ramp gradually toward
     a peak around 6 seconds. A user holding for 1-2 seconds spends 3-6
     hearts; a user committing to a long hold can still empty their
     wallet but it takes ~15 seconds for 20 hearts. */
  /* R444 (Jan): "for non-logged in users dont show this out of hearts
     notification ... let them heart each character once and have the heart be
     filled in and thats it. if they click it again nothing happens."
     THE SOURCE OF THE TOAST IN HER SCREENSHOT. This module owns pointerdown on
     every heart button: it preventDefaults, suppresses the synthesized click,
     and fires the heart itself. Its first act inside fire() is a wallet check,
     and a signed-out visitor has no wallet, so heartsRemainingToday() answers 0
     and it announced "Out of hearts" before hohHeart was ever reached. The
     anonymous path in hoh-section-core never got a say.
     A visitor has nothing to stream either. One heart per hero is the whole
     mechanic, so this module simply stands down for them: no preventDefault,
     no click suppression, no hold. The plain inline onclick on the button then
     runs hohHeart -> hohAnonHeart, which gives the one heart and fills it.
     Signed in, nothing changes. */
  function anonVisitor(){
    try { return (typeof window.hohIsHallSignedIn === 'function') && !window.hohIsHallSignedIn(); }
    catch(_){ return false; }
  }
  /* R793 (Jan, 2026-09-22): a heart is a personal favourite now, one per
     account and hero, and nothing counts up. There is nothing to stream,
     pour or double-tap, so in the live Hall this module stands down the
     same way it does for a visitor: no preventDefault, no click
     suppression, no hold, no bulk slider. The button's own onclick runs
     hohHeart, which toggles the favourite. Legacy mode (the R793B doors
     not installed yet) keeps every gesture below exactly as it was. */
  function favouritesOnly(){
    try { return (typeof window.hohR793Live === 'function') && window.hohR793Live(); }
    catch(_){ return false; }
  }
  function standDown(){ return anonVisitor() || favouritesOnly(); }

  var INITIAL_INTERVAL = 320;   /* first cadence (~3/sec) */
  var MIN_INTERVAL = 80;        /* mid-ramp cadence (~12/sec) */
  var ULTRA_INTERVAL = 50;      /* peak cadence (~20/sec) */
  var ACCEL_PERIOD = 4000;      /* time to ramp from INITIAL → MIN (4s) */
  var ULTRA_AT = 6000;          /* time after which ULTRA kicks in (6s) */
  var state = null;

  function startHold(btn, charId, detailMode, xpMode){
    /* If a hold is somehow still in flight (rare: pointerup didn't
       fire, multi-touch on mobile, browser quirk), use the per-hero
       contract above:
         same hero AND same mode → ignore the new press, the existing
                                    hold owns it
         other hero OR other mode → force-stop the stale hold, then
                                    start fresh so the user can
                                    immediately spend at the slow
                                    baseline (no inherited ramp).
       R148-Z22c: Mode is included in the dedup key so a user can
       switch between hearting a hero (main hall) and feeding XP to
       the SAME hero (Your Hall pill) without the second gesture
       getting eaten by the first one's dedup. */
    if(state){
      if(state.charId === charId && state.xpMode === !!xpMode) return;
      stopHold();
    }
    var startMs = Date.now();
    state = { btn: btn, charId: charId, detailMode: !!detailMode, xpMode: !!xpMode, timeoutId: null, count: 0 };
    /* R148-Z22c: Reset the XP-mode failure counter at gesture start
       so a stale counter from a previous gesture (where pointerup
       missed somehow) doesn't immediately dead-lock the new hold. */
    if(xpMode && btn) btn._xpHoldFailCount = 0;
    var fire = function(){
      if(!state || state.btn !== btn) return;
      /* Wallet check FIRST — if the user is out of hearts, abort the
         hold cleanly without firing OR incrementing count. This is
         the bug fix for "card disappears even when wallet is 0":
         previously state.count++ ran regardless, so stopHold's
         count > 0 branch removed the card on pointerup. Now if
         nothing was actually spent, state.count stays 0 and the
         card stays. */
      var heartsLeft = (typeof window.heartsRemainingToday === 'function') ? window.heartsRemainingToday() : 1;
      if(heartsLeft <= 0){
        /* R318: the toast used to fire only when the hold started empty —
           running DRY mid-stream ended the hold with zero feedback, so the
           player kept pressing a dead button. One toast per hold, either way. */
        if(!state._dryToasted && typeof window.hohShowHeartToast === 'function'){
          state._dryToasted = true;
          try { window.hohShowHeartToast('Out of hearts', 'You\'ve spent today\'s. More are scattered around the Hall, if you know where to look.'); } catch(_){}
        }
        /* End the hold loop here. stopHold (on pointerup) will see
           count = 0 and skip the card removal. */
        if(state.timeoutId) clearTimeout(state.timeoutId);
        state.timeoutId = null;
        return;
      }
      /* R133-B: Per-character cap pre-flight on EVERY hold tick. If
         the hero is in the capped-today ledger AND this isn't a
         Fresh Faces context (Fresh Faces bypasses cap server-side,
         so its hearts never mark a hero capped), kill the loop and
         show the cap toast. Without this, the hold keeps firing,
         each fire hits the server, server returns cap_rejected, the
         client refunds the wallet — visually loops "give → refund →
         give → refund" until pointerup.
         R148-Z22c: SKIP this check for XP mode — the per-character
         heart cap is about heart-gifting to the hero, not the
         submitter's XP-spend. There's no cap on XP spending; the
         curve scales to infinity. */
      if(!xpMode){
        try {
          if(charId && typeof window.hohIsCharCappedToday === 'function' && window.hohIsCharCappedToday(charId)){
            var isFreshContext = btn && btn.classList && btn.classList.contains('hoh-fresh-card-heart');
            if(!isFreshContext){
              /* Fire the cap rejection UI exactly once and end. */
              if(state.count === 0 && typeof window.hohHandleCapRejection === 'function'){
                try { window.hohHandleCapRejection(charId, btn); } catch(_){}
              }
              if(state.timeoutId) clearTimeout(state.timeoutId);
              state.timeoutId = null;
              return;
            }
          }
          /* R166: Same kill switch for the soft 30s cooldown. Without
             this, a held heart on a hero that just hit cap would keep
             firing hohHeart, which would soft-toast each tick and burn
             nothing — but the visual feedback would imply hearts were
             still flowing. Cleaner to end the loop the moment cooldown
             engages. */
          if(charId && typeof window.hohIsCharOnCooldown === 'function' && window.hohIsCharOnCooldown(charId)){
            var isFreshContext2 = btn && btn.classList && btn.classList.contains('hoh-fresh-card-heart');
            if(!isFreshContext2){
              if(state.count === 0 && typeof window.hohHandleCapRejection === 'function'){
                try { window.hohHandleCapRejection(charId, btn); } catch(_){}
              }
              if(state.timeoutId) clearTimeout(state.timeoutId);
              state.timeoutId = null;
              return;
            }
          }
        } catch(_){}
      }
      /* R148-Z22c (R148-Z22h: bumped 3 → 20). XP-mode dead-button
         check. The spend handler increments btn._xpHoldFailCount on
         every RPC failure, resets to 0 on success. At 3 the hold
         choked after a tiny burst even when the user expected a
         long ramp. 20 gives ~4-6s of held time before bailing —
         long enough to feel the cadence ramp (320ms → 80ms over
         4s, 50ms ultra after 6s) — while still protecting the
         server from a fully-broken-RPC infinite stream. Successful
         spends reset the counter so steady-state holds never hit
         the cap; only true sustained failures stop the loop. */
      if(xpMode && btn && (btn._xpHoldFailCount || 0) >= 20){
        if(state.timeoutId) clearTimeout(state.timeoutId);
        state.timeoutId = null;
        return;
      }
      /* R132-A: Track whether this fire actually spent a heart. The
         page-allowance / wallet gates inside the routed handlers can
         REJECT the spend (red pulse, no spend). When that happens we
         must NOT count it — otherwise stopHold's count > 0 branch
         will mark the card as spent-heart even though no heart was
         given (the "red pulse + disabled card without spending" bug
         on Fairy Ring and other small-batch themed phases). The fresh
         faces quickHeart now returns boolean; the other paths run
         optimistic visuals that count regardless (their gates are
         elsewhere). */
      var fired = true;
      try {
        if(xpMode){
          /* R148-Z22c: XP-pill route — feed XP to a character the
             user submitted. The spend function handles its own
             heart deduction, blue heart-fly sprite, bar fill,
             feeding glow, and level/tier-up celebrations. We don't
             await — fire-and-forget on each tick, the failure
             counter on btn._xpHoldFailCount lets us auto-stop
             after sustained failures (see top of fire). */
          if(typeof window.hohYhSpendHeartForXp === 'function'){
            window.hohYhSpendHeartForXp(charId, btn, null, {
              isStream: true,
              streamIdx: state.count
            });
          } else {
            fired = false;
          }
        } else if(detailMode){
          if(typeof window.hohHeartDetail === 'function') window.hohHeartDetail();
        } else if(btn && btn.classList && btn.classList.contains('hoh-fresh-card-heart')){
          /* Fresh Face heart: route through the specialized handler
             so the per-page allowance check, the daily Wander quest
             counter, and the persistent spent-state class all fire
             on every heart in the hold. Calling hohHeart directly
             skipped these because the hold path bypassed the inline
             onclick wrapper. */
          if(typeof window.hohFreshFacesQuickHeart === 'function'){
            var ok = window.hohFreshFacesQuickHeart(charId);
            if(ok === false) fired = false;
          } else if(typeof window.hohHeart === 'function'){
            window.hohHeart(charId, btn);
          }
        } else {
          if(typeof window.hohHeart === 'function') window.hohHeart(charId, btn);
        }
      } catch(e){}
      if(!fired){
        /* End the hold loop here. stopHold sees count = 0 and skips
           the after-heart visual cleanup, so the card stays
           interactive and unmarked. */
        if(state.timeoutId) clearTimeout(state.timeoutId);
        state.timeoutId = null;
        return;
      }
      state.count++;
      var n = state.count;
      if(n === 10) btn.classList.add('hoh-heart-frenzy-mid');
      if(n === 30) btn.classList.add('hoh-heart-frenzy-high');
      if(n === 60) btn.classList.add('hoh-heart-frenzy-max');
      var elapsed = Date.now() - startMs;
      var interval;
      if(elapsed >= ULTRA_AT){
        interval = ULTRA_INTERVAL;
      } else {
        var t = Math.min(elapsed / ACCEL_PERIOD, 1);
        interval = INITIAL_INTERVAL + (MIN_INTERVAL - INITIAL_INTERVAL) * t;
      }
      state.timeoutId = setTimeout(fire, interval);
    };
    fire();
  }

  /* Click-suppression flags. After pointerdown fires a heart via
     startHold, the browser still synthesizes a click event on most
     desktop browsers (Chrome, Edge, Safari). The inline onclick on
     heart buttons (hohHeart / hohHeartDetail / hohFreshFacesQuickHeart)
     would then fire a SECOND heart for the same physical click,
     doubling spend.

     The original code only suppressed Fresh Face hearts. We extend
     suppression to every heart button class the pointerdown handler
     intercepts, so a single click consumes exactly one heart in all
     contexts. */
  var suppressedBtn = null;
  var suppressUntil = 0;
  function stopHold(){
    if(!state) return;
    if(state.timeoutId) clearTimeout(state.timeoutId);
    var btn = state.btn;
    var count = state.count;
    var wasXpMode = state.xpMode;
    if(btn){
      btn.classList.remove('hoh-heart-frenzy-mid', 'hoh-heart-frenzy-high', 'hoh-heart-frenzy-max');
    }
    state = null;
    /* R148-Z22c: Notify the XP-spend module so it can drop the
       "feeding" glow on the bar and reset the failure counter.
       The class auto-removes on a timer anyway, but explicitly
       clearing on stop avoids a 500ms tail. */
    if(wasXpMode && btn && typeof window.hohYhStreamStop === 'function'){
      try { window.hohYhStreamStop(btn); } catch(_){}
    }
    /* Fresh Faces: route through the shared cleanup helper so cache
       invalidation, DOM removal, and re-render all happen the same
       way the click path handles them. Without this, hold-released
       hearts would leave stale cards in the cachedWanderingPool /
       renderedSnapshotIds and the card could re-appear on the next
       render — especially when hohHeart's RPC was slow or
       cap-rejected. */
    if(btn && btn.classList && btn.classList.contains('hoh-fresh-card-heart') && count > 0){
      suppressedBtn = btn;
      suppressUntil = Date.now() + 250;
      var card = btn.closest('.hoh-fresh-card');
      var charId = card ? card.getAttribute('data-id') : null;
      if(charId && typeof window.hohFreshFacesAfterHeart === 'function'){
        window.hohFreshFacesAfterHeart(charId);
      } else if(card){
        /* Fallback if the helper isn't loaded — old behavior. */
        card.classList.add('hoh-fresh-card-loved-out');
        setTimeout(function(){
          if(typeof window.hohRenderFreshFaces === 'function') window.hohRenderFreshFaces();
        }, 420);
      }
    }
  }
  /* Capturing-phase click handler: cancels the inline onclick that
     would otherwise fire after pointerdown already fired a heart.
     Match if the click target IS the suppressed button OR is a
     descendant (e.g. clicked the inner span / SVG / count digit).
     Works for any class — not just Fresh Faces. */
  document.addEventListener('click', function(e){
    if(!suppressedBtn) return;
    if(Date.now() > suppressUntil){ suppressedBtn = null; return; }
    var t = e.target;
    var hit = t === suppressedBtn || (suppressedBtn.contains && suppressedBtn.contains(t));
    if(hit){
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      suppressedBtn = null;
    }
  }, true);

  /* R267: tracks the last grid-heart tap so a quick second tap opens the
     bulk-heart slider instead of firing another single heart. */
  var _bulkLastId = null, _bulkLastTs = 0;

  document.addEventListener('pointerdown', function(e){
    /* R148-Z22c: Selector now includes the Your-Hall level pill
       (.hoh-yh-quest-level[data-yh-spend]) so XP-pill spends route
       through the same ramping cadence + auto-stop logic as the
       main hall heart-spam. The pill is rendered with data-yh-spend
       so this delegate only fires on the spend-enabled instances. */
    /* R252: .hoh-fresh-card-heart removed — that button is now the OPEN
       door in the rolls (the card itself hearts on tap), so hold-to-
       stream retires in Fresh Faces. Main grid + detail keep it. */
    var btn = e.target.closest('.hoh-heart-btn, #hoh-detail-heart, .hoh-detail-heart-btn, .hoh-yh-quest-level[data-yh-spend]');
    if(!btn) return;
    /* R444: before preventDefault, on purpose. Returning here leaves the
       browser's own click intact, which is the path a visitor's single heart
       travels. Bailing any later would swallow the tap entirely.
       R793: the live Hall's favourite toggle travels the same path. */
    if(standDown()) return;
    if(e.button !== undefined && e.button !== 0) return;
    /* R140-Z3k: Detail modal heart is now display-only (R140-Z3j).
       The hold-to-stream gesture should NOT fire on it — hearts only
       flow through the main grid + Explore the Hall. Skip when the
       element carries the display-only class. */
    if(btn.classList && btn.classList.contains('hoh-detail-heart-btn-display')) return;
    var detailMode = (btn.id === 'hoh-detail-heart' || btn.classList.contains('hoh-detail-heart-btn'));
    /* R148-Z22c: XP-mode flag. The pill carries data-char-id directly
       so we read it from the pill, NOT from a parent card lookup
       (the card's data-hero-id matches but the pill is the
       authoritative source for charId). */
    var xpMode = !!(btn.classList && btn.classList.contains('hoh-yh-quest-level'));
    var charId = null;
    if(xpMode){
      charId = btn.getAttribute('data-char-id');
    } else if(detailMode){
      charId = (window.currentDetail && window.currentDetail.id) || null;
    } else {
      var card = btn.closest('[data-hero-id]') || btn.closest('[data-id]');
      charId = card ? (card.getAttribute('data-hero-id') || card.getAttribute('data-id')) : null;
    }
    if(!charId) return;
    e.preventDefault();
    e.stopPropagation();
    /* Mark this button as click-suppressed for the next ~300ms so
       the synthesized click after pointerup doesn't re-fire the
       inline onclick handler. Covers desktop (Chrome / Edge / Safari)
       where preventDefault on pointerdown does NOT prevent click. */
    suppressedBtn = btn;
    suppressUntil = Date.now() + 300;
    /* R267: double-tap the GRID heart opens the bulk-heart slider. The
       first tap already gave one heart the normal way; a second tap on the
       same heart within 320ms opens the popup instead of adding another,
       and never starts a hold. Detail / XP / Fresh Faces hearts are
       untouched (they keep their own behavior). */
    if(!detailMode && !xpMode && btn.classList && btn.classList.contains('hoh-heart-btn')){
      var _bnow = Date.now();
      if(_bulkLastId === charId && (_bnow - _bulkLastTs) < 320){
        _bulkLastId = null; _bulkLastTs = 0;
        suppressUntil = Date.now() + 420;
        try{ if(typeof window.hohOpenBulkHeart === 'function') window.hohOpenBulkHeart(charId, btn); }catch(_){}
        return;
      }
      _bulkLastId = charId; _bulkLastTs = _bnow;
    }
    startHold(btn, charId, detailMode, xpMode);
  }, { passive: false });

  ['pointerup', 'pointerleave', 'pointercancel'].forEach(function(ev){
    document.addEventListener(ev, stopHold);
  });

  /* ============================================================
     MOBILE WHOLE-CARD GESTURES
     ============================================================
     On mobile (touch input + narrow viewport), the entire main grid
     card becomes more touch-friendly:
       Single tap   → opens the detail modal (existing card behavior)
       Double tap   → fires one heart (no need to aim for the small btn)
       Long press   → starts the heart-combo stream (same as button hold)

     Detection rules:
       - Pointerdown on .hoh-card body (excluding the heart button,
         dismiss X, FF badge, etc.)
       - Long press = pointer held > 350ms without moving > 8px
       - Double tap = pointerup within 250ms of previous pointerup on
         the same card, no long press triggered
       - Single tap = anything else → fallthrough to the card's
         existing onclick (opens detail)
  */
  var GESTURE_LONG_MS = 350;
  var GESTURE_DOUBLE_MS = 250;
  var GESTURE_MOVE_TOL = 8;
  var lastTap = { id: null, ts: 0 };
  var pending = null;

  function isTouchInput(e){
    return e.pointerType === 'touch' || e.pointerType === 'pen';
  }
  function isNarrow(){
    try { return window.matchMedia('(max-width: 768px)').matches; }
    catch(e){ return window.innerWidth <= 768; }
  }
  function nearestHeartBtn(card){
    return card ? card.querySelector('.hoh-heart-btn') : null;
  }

  document.addEventListener('pointerdown', function(e){
    if(!isTouchInput(e) || !isNarrow()) return;
    /* R444: the card long-press and the double-tap both end in a spend the
       visitor cannot make. Their single tap opens the hero, which is the right
       thing for someone still deciding whether to sign in.
       R793: in the live Hall there is no spend at all. */
    if(standDown()) return;
    /* Skip if already on a button-like target (heart, dismiss). The
       button-level handler above already owns those interactions. */
    if(e.target.closest('button, a, .hoh-card-dismiss, .hoh-card-restore, .hoh-ff-badge')) return;
    var card = e.target.closest('.hoh-card[data-hero-id]');
    if(!card) return;
    var charId = card.getAttribute('data-hero-id');
    if(!charId) return;
    pending = {
      card: card,
      charId: charId,
      x: e.clientX, y: e.clientY,
      ts: Date.now(),
      longTimer: null,
      didLong: false
    };
    pending.longTimer = setTimeout(function(){
      if(!pending) return;
      pending.didLong = true;
      var btn = nearestHeartBtn(pending.card) || pending.card;
      startHold(btn, pending.charId, false);
    }, GESTURE_LONG_MS);
  }, { passive: true });

  document.addEventListener('pointermove', function(e){
    if(!pending) return;
    var dx = e.clientX - pending.x, dy = e.clientY - pending.y;
    if(dx*dx + dy*dy > GESTURE_MOVE_TOL * GESTURE_MOVE_TOL){
      /* Treat as a scroll gesture — cancel the long-press intent. */
      if(pending.longTimer) clearTimeout(pending.longTimer);
      pending = null;
    }
  }, { passive: true });

  document.addEventListener('pointerup', function(e){
    if(!pending) return;
    var p = pending;
    if(p.longTimer) clearTimeout(p.longTimer);
    pending = null;
    if(p.didLong){ /* handled by hold mechanic */ return; }
    /* Single vs double tap. */
    var now = Date.now();
    var isDouble = (lastTap.id === p.charId) && (now - lastTap.ts < GESTURE_DOUBLE_MS);
    if(isDouble){
      lastTap = { id: null, ts: 0 };
      e.preventDefault();
      var btn = nearestHeartBtn(p.card) || p.card;
      try {
        if(typeof window.hohHeart === 'function') window.hohHeart(p.charId, btn);
      } catch(err){}
    } else {
      lastTap = { id: p.charId, ts: now };
      /* Single tap: let the card's onclick open the detail. We do
         nothing; the synthetic click will fire naturally. */
    }
  }, { passive: false });
})();
